import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Save, Image as ImageIcon } from "lucide-react";
import { DatePicker } from "@/components/DatePicker";
import { resolveMediaUrl } from "@/lib/ddfApi";
import { TerminalSelect } from "@/components/admin/promos/TerminalSelect";

/**
 * EditPromoDialog — the in-place editor for an existing promo item.
 * Extracted from AdminPanel.jsx so the form, scroll-area and sticky bottom
 * action row live in one focused, testable component.
 *
 * Props:
 *   editing      — { location, index, item } | null
 *   setEditing   — setter for the above
 *   onSave       — invoked when the Save button is pressed
 *   editImgRef   — file-input ref for "Replace image" upload
 */
export default function EditPromoDialog({ editing, setEditing, onSave, editImgRef }) {
  const patch = (patchObj) =>
    setEditing({ ...editing, item: { ...editing.item, ...patchObj } });

  return (
    <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
      <DialogContent className="max-w-md max-h-[90vh] p-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-5 pt-5 pb-2 shrink-0">
          <DialogTitle>Edit Promo Item</DialogTitle>
        </DialogHeader>
        {editing && (
          <>
            <div className="px-5 pb-3 pt-2 space-y-2 overflow-y-auto flex-1">
              {editing.item.imageOnly && (
                <>
                  <Label>Image Name (used in analytics reports)</Label>
                  <Input
                    value={editing.item.imageName || ""}
                    onChange={(e) => patch({ imageName: e.target.value })}
                    placeholder={editing.item.imageFileName || "e.g. Gold Special Offers May"}
                    maxLength={80}
                    data-testid="admin-edit-promo-image-name"
                  />
                </>
              )}
              <Label>Name</Label>
              <Input
                value={editing.item.name || ""}
                onChange={(e) => patch({ name: e.target.value })}
              />
              <Label>Category</Label>
              <Input
                value={editing.item.category || ""}
                onChange={(e) => patch({ category: e.target.value })}
              />
              <Label>Description</Label>
              <Textarea
                value={editing.item.description || ""}
                onChange={(e) => patch({ description: e.target.value })}
              />
              <Label>Header</Label>
              <Input
                value={editing.item.header || ""}
                onChange={(e) => patch({ header: e.target.value })}
              />
              <Label>Ribbon Color</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="color"
                  value={editing.item.ribbonColor || "#e63946"}
                  onChange={(e) => patch({ ribbonColor: e.target.value })}
                  className="w-12 h-9 p-1"
                  data-testid="admin-edit-promo-ribbon-color"
                />
                <Input
                  type="text"
                  value={editing.item.ribbonColor || "#e63946"}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (/^#[0-9a-fA-F]{0,6}$/.test(v)) patch({ ribbonColor: v });
                  }}
                  placeholder="#e63946"
                  className="w-24 h-9 font-mono text-xs"
                  maxLength={7}
                  data-testid="admin-edit-promo-ribbon-color-hex"
                />
              </div>
              <Label>Ribbon Text Color</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="color"
                  value={editing.item.ribbonTextColor || "#ffffff"}
                  onChange={(e) => patch({ ribbonTextColor: e.target.value })}
                  className="w-12 h-9 p-1"
                  data-testid="admin-edit-promo-ribbon-text-color"
                />
                <Input
                  type="text"
                  value={editing.item.ribbonTextColor || "#ffffff"}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (/^#[0-9a-fA-F]{0,6}$/.test(v)) patch({ ribbonTextColor: v });
                  }}
                  placeholder="#ffffff"
                  className="w-24 h-9 font-mono text-xs"
                  maxLength={7}
                  data-testid="admin-edit-promo-ribbon-text-color-hex"
                />
              </div>
              <Label>Price (AED)</Label>
              <Input
                type="number"
                value={editing.item.priceAED || 0}
                onChange={(e) => patch({ priceAED: parseFloat(e.target.value) || 0 })}
              />
              <Label>WAS Price (AED)</Label>
              <Input
                type="number"
                value={editing.item.wasPrice || 0}
                onChange={(e) => patch({ wasPrice: parseFloat(e.target.value) || 0 })}
              />
              <Label>Validity (Valid until)</Label>
              <DatePicker
                value={editing.item.validUntil || ""}
                onChange={(v) => patch({ validUntil: v })}
                placeholder="Pick a valid-until date"
                testId="admin-edit-promo-validuntil"
              />
              <Label>Terminal / Shop name (optional)</Label>
              <TerminalSelect
                value={editing.item.terminal}
                specific={editing.item.terminalSpecific}
                onChange={(terminal, terminalSpecific) => patch({ terminal, terminalSpecific })}
                testId="admin-edit-promo-terminal"
              />
              <Label>Corner Tag (top-right badge, optional)</Label>
              <Input
                value={editing.item.cornerTag || ""}
                onChange={(e) => patch({ cornerTag: e.target.value })}
                placeholder="e.g. OFFER, NEW, TRAVEL EXCLUSIVE"
                data-testid="admin-edit-promo-corner-text"
              />
              {editing.item.cornerTag ? (
                <div className="flex items-center gap-2">
                  <Label className="flex-1 text-xs">Tag background</Label>
                  <Input
                    type="color"
                    value={editing.item.cornerTagColor || "#111827"}
                    onChange={(e) => patch({ cornerTagColor: e.target.value })}
                    className="w-12 h-9 p-1"
                    data-testid="admin-edit-promo-corner-color"
                  />
                  <Input
                    type="color"
                    value={editing.item.cornerTagTextColor || "#ffffff"}
                    onChange={(e) => patch({ cornerTagTextColor: e.target.value })}
                    className="w-12 h-9 p-1"
                    data-testid="admin-edit-promo-corner-text-color"
                  />
                </div>
              ) : null}
              <Label>Publish status</Label>
              <Select
                value={editing.item.status || "published"}
                onValueChange={(v) => patch({ status: v })}
              >
                <SelectTrigger data-testid="admin-edit-promo-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="published">Published (live)</SelectItem>
                  <SelectItem value="holding">Holding</SelectItem>
                </SelectContent>
              </Select>
              {(editing.item.status || "published") === "holding" && (
                <>
                  <Label>Scheduled publish date &amp; time (optional)</Label>
                  <Input
                    type="datetime-local"
                    value={(editing.item.publishAt || "").slice(0, 16)}
                    onChange={(e) => patch({ publishAt: e.target.value })}
                    data-testid="admin-edit-promo-publishat"
                  />
                </>
              )}
              <Label>Replace Image (optional)</Label>
              <Input type="file" accept="image/*" ref={editImgRef} />
            </div>

            {/* Sticky bottom action row — image preview on the left, Cancel + Save on the right. */}
            <div className="flex items-center gap-3 px-5 py-3 border-t bg-white shrink-0">
              <div className="shrink-0">
                {editing.item.image ? (
                  <img
                    src={resolveMediaUrl(editing.item.image)}
                    alt=""
                    className="h-16 w-16 object-contain rounded"
                    data-testid="admin-edit-promo-preview"
                  />
                ) : (
                  <div className="h-16 w-16 rounded border bg-gray-50 flex items-center justify-center text-gray-400">
                    <ImageIcon size={22} />
                  </div>
                )}
              </div>
              <div className="flex-1" />
              <Button
                variant="outline"
                onClick={() => setEditing(null)}
                data-testid="admin-edit-promo-cancel"
              >
                Cancel
              </Button>
              <Button onClick={onSave} data-testid="admin-edit-promo-save">
                <Save size={14} className="mr-1" /> Save
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
